/**
 * usePermissions Hook
 * 
 * Gets permissions object for current user's role
 * Usage: const permissions = usePermissions(userRole)
 *        if (permissions.canDeleteEntries) { ... }
 */

import { useMemo } from 'react'
import type { Role, Permissions } from '@/lib/types'
import { roleToPermissions } from '@/lib/permissions'

/**
 * Hook to get permissions for a given role
 * 
 * @param role - The user's role (can be null/undefined)
 * @returns Permissions object with all capabilities
 * 
 * @example
 * const permissions = usePermissions(user.role)
 * 
 * return (
 *   <>
 *     {permissions.canDeleteEntries && (
 *       <button onClick={handleDelete}>Delete</button>
 *     )}
 *   </>
 * )
 */
export function usePermissions(role: Role | null | undefined): Permissions {
  return useMemo(() => {
    return roleToPermissions(role)
  }, [role])
}
